import { useState, useMemo } from "react";
import { MoreHorizontal, Calendar, RefreshCw, Loader2, ArrowUp, ArrowDown, Check, X } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import type { RegularStudent } from "@/hooks/useRegularStudents";
import type { ScheduleFilter, RegularStudentSortField, SortOrder } from "@/hooks/useRegularStudentFilters";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { StudentScheduleCalendarModal } from "./StudentScheduleCalendarModal";

function formatThaiDate(dateStr: string | null) {
  if (!dateStr) return "-";
  const d = new Date(dateStr);
  const thaiMonths = ["ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.", "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."];
  return `${d.getDate()} ${thaiMonths[d.getMonth()]} ${d.getFullYear() + 543 - 2500}`;
}

interface Props {
  students: RegularStudent[];
  searchQuery: string;
  scheduleFilter: ScheduleFilter;
  sortField: RegularStudentSortField;
  sortOrder: SortOrder;
  onSort: (field: RegularStudentSortField) => void;
}

function SortableHead({ field, label, sortField, sortOrder, onSort, className }: {
  field: RegularStudentSortField;
  label: string;
  sortField: RegularStudentSortField;
  sortOrder: SortOrder;
  onSort: (f: RegularStudentSortField) => void;
  className?: string;
}) {
  const active = sortField === field;
  return (
    <TableHead
      className={`cursor-pointer select-none hover:bg-muted/50 transition-colors ${className ?? ""}`}
      onClick={() => onSort(field)}
    >
      <div className="flex items-center gap-1">
        {label}
        {active && (sortOrder === "asc" ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />)}
      </div>
    </TableHead>
  );
}

export function RegularStudentsTable({ students, searchQuery, scheduleFilter, sortField, sortOrder, onSort }: Props) {
  const [selectedStudent, setSelectedStudent] = useState<RegularStudent | null>(null);
  const [recalculating, setRecalculating] = useState<string | null>(null);
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const filtered = useMemo(() => {
    let list = students;

    // text search
    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      list = list.filter((s) =>
        s.code.toLowerCase().includes(q) ||
        s.first_name.toLowerCase().includes(q) ||
        (s.nickname ?? "").toLowerCase().includes(q)
      );
    }

    // schedule filter
    if (scheduleFilter === "has_schedule") {
      list = list.filter((s) => s.weekly_scheduled_hours != null && s.weekly_scheduled_hours > 0);
    } else if (scheduleFilter === "no_schedule") {
      list = list.filter((s) => !s.weekly_scheduled_hours || s.weekly_scheduled_hours <= 0);
    }

    // sort
    const sorted = [...list].sort((a, b) => {
      let cmp = 0;
      switch (sortField) {
        case "name":
          cmp = a.first_name.localeCompare(b.first_name, "th");
          break;
        case "code":
          cmp = a.code.localeCompare(b.code);
          break;
        case "hours":
          cmp = (a.hours ?? -Infinity) - (b.hours ?? -Infinity);
          break;
        case "weekly_scheduled_hours":
          cmp = (a.weekly_scheduled_hours ?? -Infinity) - (b.weekly_scheduled_hours ?? -Infinity);
          break;
        case "estimated_hours_depletion_date":
          cmp = (a.estimated_hours_depletion_date ?? "").localeCompare(b.estimated_hours_depletion_date ?? "");
          break;
        case "last_class_date":
          cmp = (a.last_class_date ?? "").localeCompare(b.last_class_date ?? "");
          break;
        case "has_classes_last_30_days":
          cmp = (a.has_classes_last_30_days ? 1 : 0) - (b.has_classes_last_30_days ? 1 : 0);
          break;
      }
      return sortOrder === "asc" ? cmp : -cmp;
    });

    return sorted;
  }, [students, searchQuery, scheduleFilter, sortField, sortOrder]);

  const handleRecalculate = async (code: string) => {
    setRecalculating(code);
    try {
      const { error } = await supabase.functions.invoke("calculate-hours-depletion", {
        body: { student_codes: [code] },
      });
      if (error) throw error;
      toast({ title: "คำนวณเสร็จสิ้น", description: `อัพเดตวันหมดชั่วโมงของ ${code} แล้ว` });
      queryClient.invalidateQueries({ queryKey: ["regular-students"] });
    } catch {
      toast({ title: "เกิดข้อผิดพลาด", description: "ไม่สามารถคำนวณได้", variant: "destructive" });
    } finally {
      setRecalculating(null);
    }
  };

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[40px]">#</TableHead>
              <SortableHead field="name" label="ชื่อ" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
              <SortableHead field="code" label="รหัส" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
              <SortableHead field="hours" label="ชม.คงเหลือ" sortField={sortField} sortOrder={sortOrder} onSort={onSort} className="text-right" />
              <SortableHead field="weekly_scheduled_hours" label="ชม./สัปดาห์" sortField={sortField} sortOrder={sortOrder} onSort={onSort} className="text-right" />
              <SortableHead field="estimated_hours_depletion_date" label="วันที่คาดว่าจะหมด" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
              <SortableHead field="last_class_date" label="เรียนล่าสุด" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
              <SortableHead field="has_classes_last_30_days" label="30 วันล่าสุด" sortField={sortField} sortOrder={sortOrder} onSort={onSort} />
              <TableHead className="w-[60px]"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filtered.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground py-8">
                  ไม่พบข้อมูล
                </TableCell>
              </TableRow>
            ) : (
              filtered.map((s, i) => (
                <TableRow key={s.code}>
                  <TableCell className="text-muted-foreground">{i + 1}</TableCell>
                  <TableCell>
                    <div className="font-medium">{s.first_name} {s.last_name ?? ""}</div>
                    {s.nickname && <div className="text-sm text-muted-foreground">({s.nickname})</div>}
                  </TableCell>
                  <TableCell className="font-mono text-sm">{s.code}</TableCell>
                  <TableCell className="text-right font-semibold">
                    <span className={s.hours != null && s.hours <= 0 ? "text-destructive" : s.hours != null && s.hours <= 8 ? "text-orange-600" : ""}>
                      {s.hours != null ? s.hours.toFixed(1) : "-"}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">{s.weekly_scheduled_hours ?? "-"}</TableCell>
                  <TableCell>{formatThaiDate(s.estimated_hours_depletion_date)}</TableCell>
                  <TableCell>{formatThaiDate(s.last_class_date)}</TableCell>
                  <TableCell>
                    {s.has_classes_last_30_days ? (
                      <Badge variant="success" className="gap-1"><Check className="h-3 w-3" />มีเรียน</Badge>
                    ) : (
                      <Badge variant="destructive" className="gap-1"><X className="h-3 w-3" />ไม่มีเรียน</Badge>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          {recalculating === s.code ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setSelectedStudent(s)}>
                          <Calendar className="mr-2 h-4 w-4" />
                          ตารางเรียน
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => handleRecalculate(s.code)} disabled={recalculating === s.code}>
                          <RefreshCw className="mr-2 h-4 w-4" />
                          คำนวณวันหมดชั่วโมง
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      <StudentScheduleCalendarModal
        student={selectedStudent}
        onClose={() => setSelectedStudent(null)}
      />
    </>
  );
}
